/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        useMetar.ts
 * @description Hook React pour la recuperation et le rafraichissement automatique
 *              des donnees METAR (conditions meteorologiques aeroport)
 *
 * @features    - Rafraichissement automatique toutes les 5 minutes
 *              - Cache des donnees via TanStack Query
 *              - Gestion des erreurs et fallback
 *              - Support multi-aeroports
 *
 * @version     2.3
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { metarService, MetarData } from '@/services/metarService';

// Intervalle de rafraichissement (5 minutes = 300000 ms)
// Les donnees METAR sont MAJ toutes les 30 min, mais on rafraichit plus souvent pour avoir les dernieres
const METAR_REFRESH_INTERVAL = 5 * 60 * 1000;

// Duree de cache (15 minutes)
const METAR_STALE_TIME = 15 * 60 * 1000;

/**
 * Hook pour recuperer les donnees METAR actuelles
 *
 * @param icao Code ICAO de l'aeroport (defaut: LFPO pour Paris-Orly)
 * @param enabled Activer/desactiver la requete
 * @returns Donnees METAR, etat de chargement, erreur
 *
 * @example
 * const { data: metar, isLoading, error } = useMetar('LFPO');
 * // metar?.temperature, metar?.wind_speed, etc.
 */
export function useMetar(icao: string = 'LFPO', enabled: boolean = true) {
  return useQuery({
    queryKey: ['metar', icao],
    queryFn: () => metarService.getCurrent(icao),
    enabled,
    refetchInterval: METAR_REFRESH_INTERVAL,
    staleTime: METAR_STALE_TIME,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * Hook pour recuperer les indicateurs hiver detailles
 *
 * @param icao Code ICAO de l'aeroport
 * @param enabled Activer/desactiver la requete
 * @returns Donnees hiver detaillees
 *
 * @example
 * const { data: winter } = useMetarWinter('LFPO');
 * // winter?.risques.givrage, winter?.alert.level, etc.
 */
export function useMetarWinter(icao: string = 'LFPO', enabled: boolean = true) {
  return useQuery({
    queryKey: ['metar-winter', icao],
    queryFn: () => metarService.getWinterIndicators(icao),
    enabled,
    refetchInterval: METAR_REFRESH_INTERVAL,
    staleTime: METAR_STALE_TIME,
    retry: 2,
  });
}

/**
 * Hook pour forcer le rafraichissement des donnees METAR
 *
 * @returns Fonction de rafraichissement
 */
export function useRefreshMetar() {
  const queryClient = useQueryClient();

  return async (icao: string = 'LFPO') => {
    // Invalider le cache
    await queryClient.invalidateQueries({ queryKey: ['metar', icao] });
    await queryClient.invalidateQueries({ queryKey: ['metar-winter', icao] });

    // Forcer un nouveau fetch
    const data = await metarService.refresh(icao);
    return data;
  };
}

/**
 * Retourne la couleur de fond selon le niveau d'alerte
 */
export function getAlertColorClass(level: string): string {
  switch (level) {
    case 'critique':
      return 'bg-red-100 border-red-300 text-red-800';
    case 'warning':
      return 'bg-amber-100 border-amber-300 text-amber-800';
    case 'vigilance':
      return 'bg-yellow-100 border-yellow-300 text-yellow-800';
    default:
      return 'bg-green-100 border-green-300 text-green-800';
  }
}

/**
 * Retourne l'icone meteo selon les conditions
 */
export function getWeatherIcon(metar: MetarData | null): string {
  if (!metar) return '🌡️';

  const temp = metar.temperature ?? 0;
  const weatherReport = metar.weather_report?.toLowerCase() ?? '';
  const visibility = metar.visibility ?? 9999;

  // Neige
  if (weatherReport.includes('neige') || weatherReport.includes('snow')) {
    return '🌨️';
  }

  // Brouillard
  if (visibility < 1000 || weatherReport.includes('brouillard') || weatherReport.includes('fog')) {
    return '🌫️';
  }

  // Pluie
  if (weatherReport.includes('pluie') || weatherReport.includes('rain')) {
    return '🌧️';
  }

  // Nuageux
  if (weatherReport.includes('nuage') || weatherReport.includes('cloud')) {
    if (weatherReport.includes('couvert') || weatherReport.includes('overcast')) {
      return '☁️';
    }
    return '⛅';
  }

  // Clair et froid
  if (temp < 0) {
    return '❄️';
  }

  // CAVOK (ciel clair)
  if (metar.cavok) {
    return '☀️';
  }

  return '🌡️';
}

/**
 * Formate la visibilite pour affichage
 */
export function formatVisibility(visibility: number | null): string {
  if (visibility === null) return 'N/A';
  if (visibility >= 9999) return '> 10 km';
  if (visibility >= 1000) return `${(visibility / 1000).toFixed(1)} km`;
  return `${visibility} m`;
}

/**
 * Formate le vent pour affichage compact
 */
export function formatWind(metar: MetarData | null): string {
  if (!metar || metar.wind_speed === null) return 'Calme';

  let windStr = `${metar.wind_dir_text} ${metar.wind_speed} kt`;

  if (metar.wind_gust && metar.wind_gust > metar.wind_speed) {
    windStr += ` (raf. ${metar.wind_gust} kt)`;
  }

  return windStr;
}

export default useMetar;
